/**
 * Keystone RFP Agent Configuration
 * Comprehensive configuration for RFP proposal automation with EGBC compliance
 * 
 * This configuration extends TraceKit core to provide RFP-specific workflows:
 * - Requirement extraction from RFP documents
 * - Past proposal search and evidence retrieval
 * - Draft generation with evidence citations
 * - Word count optimization
 * - Professional review workflow
 * - Compliance monitoring
 */

import { baseConfig } from './base.config';
import { TraceKitConfig } from '../packages/core/src/types/config';
import { keystoneThemeConfig } from '../packages/apps/keystone-rfp/src/config/theme';

// Keystone RFP Agent specific IDs (from on-premise database)
// These defaults can be overridden via environment variables in main.tsx
// For Keystone On-Premise deployment, set VITE_TENANT_ID and VITE_PROJECT_ID in launch.json
export const KEYSTONE_TENANT_ID = 'ea83c0d8-4386-4578-ad0e-e0e3853f20ee'; // Keystone RFP Agent (On-Prem) - fallback
export const KEYSTONE_PROJECT_ID = '8df784d6-6913-4605-ae7f-0d6c6369d709'; // Keystone RFP Exploration - Document Library - fallback

export const keystoneConfig: TraceKitConfig = {
  ...baseConfig,
  
  branding: {
    appName: 'Keystone RFP Agent',
    logo: '/keystone-fulllogo.png',
    logoUrl: '/keystone-icon.jpg',
    logoAlt: 'Keystone Environmental Logo',
    favicon: '/keystone-favicon.ico',
    primaryColor: '#1e40af', // Keystone blue (blue-800)
    secondaryColor: '#3b82f6', // Keystone blue (blue-500)
    companyName: 'Keystone Environmental Ltd.',
    platformTitle: 'Keystone RFP Agent',
    platformSubtitle: 'Proposal Intelligence System',
  },
  
  theme: keystoneThemeConfig,
  
  features: {
    ...baseConfig.features,
    rag: true,              // Required for requirement extraction and proposal search
    reports: true,          // Core feature - RFPs are reports (report_type='rfp_proposal')
    entities: true,          // ENABLED - Policy Pack v2.0 has 20+ entity types with 356 fields
    sources: true,           // Required for RFP document upload and management
    jobs: true,             // Required for document ingestion and report generation
    webhooks: false,        // Not using webhooks for RFP workflow
    cache: true,            // Enable caching for performance (query results, evidence)
    admin: false,           // Admin features not needed for RFP workflow
  },
  
  deployment: {
    environment: 'production',
    domain: 'keystone-rfp.alpyne.ai',
    enableAnalytics: true,
  },
  
  customization: {
    defaultTenantId: KEYSTONE_TENANT_ID,
    defaultProjectId: KEYSTONE_PROJECT_ID,
    hideTenantSelector: false, // Show tenant selector for development/testing
    
    // Feature flags - hide advanced features for Keystone deployment
    // These features remain available in the base rfp-agent plugin for other deployments
    // Can be re-enabled by setting flags to true for custom dev timeline upsell
    featureFlags: {
      enableCalendarView: false,              // Calendar view in opportunities dashboard
      enableCustomSections: true,             // Custom sections tab in reports - ENABLED
      enableWordCountOptimizer: false,        // Word count optimizer (counts always visible)
      enableQualityAssurance: true,          // Quality assurance tab in reports - ENABLED
      enableReviewWorkflow: true,             // Review workflow tab in reports - ENABLED
      enableAvailabilityCalendar: false,       // Availability calendar tab in staff management
      enableQualificationsCerts: false,        // Qualifications & certifications tab in staff management
      enableExecutiveDashboard: false,         // Executive dashboard page (already hidden via sidebar filter)
      enableWorkPlanExecution: true,            // Work plan execution dashboard page (now enabled)
    },
    
    // RFP-specific search sample queries for UI suggestions
    searchSampleQueries: [
      // Requirement Extraction Queries
      'Extract all mandatory requirements from this RFP with section and page numbers',
      'List all technical requirements with their specifications and evaluation criteria',
      'Identify all submission requirements including format, deadline, and word limits',
      'Extract all administrative requirements (forms, certifications, etc.)',
      'List all commercial requirements (pricing, payment terms, etc.)',
      'Identify all legal requirements (liability, insurance, etc.)',
      'Extract evaluation criteria and scoring methodology',
      'List all deliverables and their specifications',
      
      // Past Proposal Search Queries
      'Find past proposals for Phase II ESA projects',
      'Search for similar contaminated sites remediation proposals',
      'Show past performance examples for environmental monitoring projects',
      'Find winning proposals for [specific client/agency]',
      'Search for proposals similar to this RFP scope',
      'Show past proposals with similar technical requirements',
      'Find proposals in the Contaminated Sites service line',
      'Search for proposals with similar project value range',
      'Find past proposals for industrial site assessments',
      'Show past performance for regulatory compliance projects',
      
      // Draft Generation Queries
      'Generate executive summary highlighting Keystone\'s expertise for [project type]',
      'Create technical approach section based on past performance and SOPs',
      'Write past performance section with specific project examples similar to this RFP',
      'Generate project team section with relevant qualifications',
      'Create schedule and deliverables section based on RFP requirements',
      'Write company overview emphasizing capabilities relevant to this project',
      
      // Evidence Retrieval Queries
      'Find evidence supporting [specific claim]',
      'Retrieve past performance examples for [project type]',
      'Find SOPs relevant to [technical methodology]',
      'Search for similar projects completed by Keystone',
      'Find evidence of [specific qualification or certification]',
      
      // General RFP Workflow Queries
      'What are the key environmental requirements?',
      'Show me compliance documents for water permits',
      'Generate RFP response for air quality monitoring',
      'Recent regulatory changes affecting our permits',
      'What documents do we need for renewal?',
      'Summarize environmental impact assessment requirements',
    ],
    
    // RFP-specific entity types (Policy Pack v2.0 - 22 entity types, 500+ fields)
    // NOTE: Entity types are now dynamically loaded from the policy pack by SearchConfigProvider
    // This list is kept for reference and backward compatibility only
    entityTypes: [
      // Government Opportunities (3) - ENHANCED with 100+ fields for comprehensive data capture
      'govt_opportunity',        // Enhanced: Now captures budget, timeline, contacts, requirements, evaluation criteria
      'govt_grant',
      'govt_contract_award',
      
      // Internal Documents (5)
      'proposal_document',
      'workplan_document',
      'rfp_document',
      'bid_document',
      'submission_document',
      
      // Company Intelligence (5)
      'staff_member',
      'past_project',
      'client_record',
      'capability_statement',
      'sop_reference',
      
      // Opportunity Analysis (3) - ADDED rfp_requirement
      'opportunity_requirement',
      'opportunity_match_score',
      'rfp_requirement',         // NEW: Individual requirements from RFP documents
      
      // Evidence & Compliance (3)
      'technical_approach',
      'past_performance_example',
      'qualification_certification',
      
      // Project Execution (3) - ADDED subcontractor
      'project_team_composition',
      'budget_estimate',
      'subcontractor',            // NEW: Subcontractor and partner information
    ],
    
    // Custom routes for RFP-specific pages (will be registered by plugin)
    customRoutes: [],
  },
  
  plugins: [
    // Plugin will be added in main.tsx:
    // import { keystoneRfpAgentPlugin } from '../../plugins/keystone-rfp-agent/src';
    // keystoneConfig.plugins = [keystoneRfpAgentPlugin];
  ],
};

/**
 * RFP-Specific Configuration Constants
 * These are used throughout the RFP Agent plugin but stored separately
 * since they don't fit in the TraceKitConfig interface
 */
export const RFP_CONFIG = {
  // Policy Pack Configuration
  policyPack: {
    id: 'keystone_rfp_agent_v1',
    version: '1.0.0',
    tenantId: KEYSTONE_TENANT_ID,
  },
  
  // Evidence Requirements
  evidence: {
    minConfidence: 0.7,
    minItemsPerSection: 3,
    preferredWinStatus: 'Won',
    recentThresholdDays: 730, // 2 years
    similarityThreshold: 0.6,
  },
  
  // Word Count Optimization
  wordOptimization: {
    enabled: true,
    preserveCitations: true,
    preserveEvidence: true,
    targetReductionPercentage: 0.15, // 15% reduction
    maxIterations: 3,
  },
  
  // Professional Review
  professionalReview: {
    required: true,
    reviewerRoles: ['P.Geo', 'P.Eng'],
    requiredBeforeStatus: 'submitted',
    auditTrailRequired: true,
  },
  
  // DOCX Export Configuration
  // Template and formatting settings for Word document generation
  // These values reference the policy pack configuration and can be overridden per tenant
  docxExport: {
    // Template path - supports both filesystem and blob storage
    // For Keystone on-premise: use filesystem path
    // For cloud deployments: use blob storage path
    templatePath: 'configs/keystone-assets/Letter_Template.docx',  // Relative to project root or absolute path
    storageType: 'filesystem' as 'filesystem' | 'blob',  // "filesystem" or "blob"
    
    // Formatting configuration per Document_Formatter.md specs
    formatting: {
      // Body text formatting
      bodyText: {
        fontName: 'Calibri',
        fontSizePt: 11,
        spacingAfterPt: 6,  // 6pt after paragraphs
        spacingBeforePt: 0,
        lineSpacing: 1.15,  // 1.15 line spacing (slightly more than single)
      },
      
      // Heading styles
      headings: {
        h1: {
          fontSizePt: 16,
          spacingBeforePt: 18,
          spacingAfterPt: 6,
        },
        h2: {
          fontSizePt: 14,
          spacingBeforePt: 12,
          spacingAfterPt: 6,
        },
        h3: {
          fontSizePt: 12,
          spacingBeforePt: 10,
          spacingAfterPt: 4,
        },
        h4: {
          fontSizePt: 11,
          spacingBeforePt: 8,
          spacingAfterPt: 4,
        },
      },
      
      // List formatting
      lists: {
        spacingBeforePt: 0,
        spacingAfterPt: 0,
        spacingAfterLastPt: 6,  // 6pt after last list item
      },
      
      // Table formatting
      tables: {
        spacingBeforePt: 12,  // 12pt before table
        spacingAfterPt: 6,  // 6pt after table
      },
      
      // Special elements
      codeBlocks: {
        spacingBeforePt: 6,
        spacingAfterPt: 6,
      },
      blockquotes: {
        spacingBeforePt: 6,
        spacingAfterPt: 6,
      },
    },
    
    // Export guardrails - formats that require approval
    exportGuardrails: {
      requireApproval: ['docx', 'pdf', 'v2'],  // DOCX, PDF, and V2 require approved/archived/submitted status
      allowedStatuses: ['approved', 'archived', 'submitted'],
    },
  },
  
  // Citation Format
  citations: {
    style: 'inline',
    format: '[{source}, p.{page}]',
    requirePageNumbers: true,
    requireSourceLinks: true,
    minCitationsPerSection: 2,
  },
  
  // Data Sources
  dataSources: {
    historicalRFPs: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 50,
    },
    historicalProposals: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 150,
    },
    keystoneSOPs: {
      enabled: true,
      syncFrequency: 'manual',
      expectedCount: 280,
    },
    onPremisesFileShares: {
      enabled: true, // X Drive accessible, I/O/P Drives require integration service
      syncFrequency: 'daily',
      accessiblePaths: [
        '\\\\key-archives\\Archives\\01 - Projects', // Archived Projects (X Drive) - 137 folders
        '\\\\key-archives\\Archives\\02 - Proposals', // Archived Proposals (X Drive) - 36 folders
      ],
      inaccessiblePaths: [
        '\\\\key-fs2012\\Common', // Active Projects (I Drive) - Requires integration service
        '\\\\key-fs2012\\Common\\A - all Promotional Projects', // Current Proposals - Requires integration service
        '\\\\key-fs2012\\Common_Ontario', // Ontario Projects (O Drive) - Requires integration service
        '\\\\key-fs2012\\Protected Records', // Finalized Reports (P Drive) - Requires integration service
        '\\\\key-fs2012\\Protected Records\\OUTLOOK PST FILES', // PST Files - Requires integration service
      ],
      // Folder structure analysis from: scripts/keystone-folder-structure-20251204-143756.json
      folderStructureAnalysis: {
        accessibleCount: 2,
        inaccessibleCount: 5,
        totalArchivedProjects: 137,
        totalArchivedProposals: 36,
        analysisDate: '2025-12-04',
      },
    },
  },
  
  // Report Types
  reportTypes: {
    rfpProposal: {
      id: 'rfp_proposal',
      defaultStatus: 'draft',
      workflowStages: ['draft', 'in_review', 'approved', 'submitted', 'archived'],
      requiredSections: [
        'Executive Summary',
        'Company Overview',
        'Technical Approach',
        'Past Performance',
        'Project Team',
        'Schedule and Deliverables',
      ],
      optionalSections: [
        'Budget',
        'Risk Management',
        'Quality Assurance',
      ],
      // RFSO prompt configuration is loaded from policy pack
      // See: configs/policy_packs/keystone_rfp_agent_v2.yaml -> report_types.rfp_proposal.template_generation_prompts
    },
  },
  
  // Service Lines
  serviceLines: [
    'Contaminated Sites Assessment & Remediation',
    'Environmental Engineering',
    'Biology & Environmental Impact Assessment',
    'Indoor Air Quality & Hazardous Materials',
    'EHS Management & Compliance',
    'Environmental Spill Response',
    'Landfill Management',
    'Forestry',
    'Indigenous Engagement',
  ],
  
  // Regulatory Frameworks
  regulatoryFrameworks: [
    'EGBC (Engineers and Geoscientists BC)',
    'CCME (Canadian Council of Ministers of the Environment)',
    'BC CSR (BC Contaminated Sites Regulation)',
    'CEPA (Canadian Environmental Protection Act)',
  ],
} as const;
